import { useState, useEffect } from "react";
import { Users, FileText, Share2, Check, ChevronRight } from "lucide-react";
import { apiClient } from "@/App";
import { tokens } from "@/design-system/tokens";

export function FirstGigSuccess({ gigId, onDismiss }) {
  const [visible, setVisible] = useState(true);
  const [highlightShare, setHighlightShare] = useState(false);

  // After 6 seconds, gently pulse the share button
  useEffect(() => {
    const timer = setTimeout(() => setHighlightShare(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = async () => {
    setVisible(false);
    try {
      await apiClient.post("/onboarding/dismiss-success");
    } catch {}
    if (onDismiss) onDismiss();
  };

  const scrollToSection = (testId) => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-orange-600", "ring-offset-2", "ring-offset-zinc-950");
      setTimeout(() => el.classList.remove("ring-2", "ring-orange-600", "ring-offset-2", "ring-offset-zinc-950"), 3000);
    }
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: `${tokens.space.xxl}px`,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 50,
        width: "calc(100% - 2rem)",
        maxWidth: "448px",
        animation: "slideInUp 500ms ease-out"
      }}
      data-testid="first-gig-success"
    >
      <div style={{
        backgroundColor: tokens.color.appBg,
        border: tokens.border.card,
        borderRadius: tokens.radius.xl,
        boxShadow: tokens.shadow.card,
        padding: `${tokens.space.lg}px`
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: `${tokens.space.md}px` }}>
          <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px` }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.color.greenSoft || "rgba(34,193,151,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Check style={{ height: "18px", width: "18px", color: tokens.color.green }} />
            </div>
            <div>
              <p style={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: "12px", color: tokens.color.text, margin: 0 }} data-testid="first-gig-headline">Your gig is ready</p>
              <p style={{ fontSize: "10px", color: tokens.color.textMuted, margin: 0 }}>Everything for this gig is now in one place.</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            style={{
              color: tokens.color.textMuted,
              fontSize: "10px",
              padding: `${tokens.space.xs}px ${tokens.space.md}px`,
              background: "none",
              border: "none",
              cursor: "pointer",
              transition: "color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
            onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
            data-testid="first-gig-dismiss"
          >
            Continue
          </button>
        </div>

        {/* Quick actions */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: `${tokens.space.md}px` }}>
          <button
            onClick={() => scrollToSection("gig-collaborators-section")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: `${tokens.space.md}px`,
              padding: "10px",
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.color.innerBg,
              border: `1px solid ${tokens.color.innerBorder}`,
              transition: "all 150ms ease",
              fontSize: "12px",
              color: tokens.color.text,
              cursor: "pointer"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.color.innerBg;
              e.currentTarget.style.borderColor = tokens.color.cardBorder;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = tokens.color.innerBg;
              e.currentTarget.style.borderColor = tokens.color.innerBorder;
            }}
            data-testid="first-gig-add-band"
          >
            <Users style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
            Add your band
          </button>
          <button
            onClick={() => scrollToSection("gig-notes-section")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: `${tokens.space.md}px`,
              padding: "10px",
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.color.innerBg,
              border: `1px solid ${tokens.color.innerBorder}`,
              transition: "all 150ms ease",
              fontSize: "12px",
              color: tokens.color.text,
              cursor: "pointer"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.color.innerBg;
              e.currentTarget.style.borderColor = tokens.color.cardBorder;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = tokens.color.innerBg;
              e.currentTarget.style.borderColor = tokens.color.innerBorder;
            }}
            data-testid="first-gig-add-notes"
          >
            <FileText style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
            Add notes
          </button>
          <button
            onClick={() => scrollToSection("share-gig-section")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: `${tokens.space.md}px`,
              padding: "10px",
              borderRadius: tokens.radius.md,
              backgroundColor: highlightShare ? "rgba(245,137,31,0.2)" : tokens.color.innerBg,
              border: `1px solid ${highlightShare ? "rgba(245,137,31,0.4)" : tokens.color.innerBorder}`,
              transition: "all 150ms ease",
              fontSize: "12px",
              color: tokens.color.text,
              cursor: "pointer",
              animation: highlightShare ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite" : "none",
              boxShadow: highlightShare ? "0 0 0 1px rgba(245,137,31,0.3)" : "none"
            }}
            onMouseEnter={(e) => {
              if (!highlightShare) {
                e.currentTarget.style.backgroundColor = tokens.color.innerBg;
                e.currentTarget.style.borderColor = tokens.color.cardBorder;
              }
            }}
            onMouseLeave={(e) => {
              if (!highlightShare) {
                e.currentTarget.style.backgroundColor = tokens.color.innerBg;
                e.currentTarget.style.borderColor = tokens.color.innerBorder;
              }
            }}
            data-testid="first-gig-share"
          >
            <Share2 style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
            Share this gig
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
